import debounce from 'debounce-promise';
import type { IObservableArray } from 'mobx';
import { when } from 'mobx';
import { useCallback, useMemo, useState } from 'react';

import type {
  Campaign,
  GeoFilter as GeoFilterModelType,
  IGeoFilter,
  Targeting,
} from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';

import {
  getCurrentGeoFilterId,
  getUsedGeoFilterIds,
  isControlled,
  type ITypeOption,
  TYPE_OPTIONS,
} from './GeoFilter.utils';

export type GeoFilterModel = GeoFilterModelType;

/**
 * Props for uncontrolled mode where the component manages its own state via targeting.
 */
export interface IUncontrolledProps {
  campaign?: Campaign;
  targeting: Targeting;
  targetings: IObservableArray<Targeting>;
  onRemove: (targeting: Targeting) => void;
  layout?: 'stacked' | 'inline';
}

/**
 * Props for controlled mode where parent manages the state.
 */
export interface IControlledProps {
  campaign?: Campaign;
  geoFilterId?: string;
  included: boolean;
  onSelect: (geoFilterId: string) => void;
  onIncludedChange: (included: boolean) => void;
  onRemove: () => void;
  layout?: 'stacked' | 'inline';
  usedGeoFilterIds?: Set<string>;
}

export type IProps = IUncontrolledProps | IControlledProps;

interface IFilters {
  is_archived__ne: boolean;
  name__icontains?: string;
  kind__in?: string[];
}

/**
 * Return type for the useGeoFilter hook.
 */
interface IUseGeoFilterReturn {
  /** The current geo filter model */
  geofilter: GeoFilterModel | undefined;
  /** The currently selected type filter */
  selectedType: ITypeOption;
  /** Debounced function to load geo filter options */
  loadOptions: (inputValue: string) => Promise<IGeoFilter[]>;
  /** Handler for included/excluded radio change */
  handleIncludedChange: (newValue?: string) => void;
  /** Handler for type filter change */
  handleTypeChange: (option?: ITypeOption) => void;
  /** Handler to clear the current selection */
  handleClear: () => void;
  /** Handler to remove this filter */
  handleRemove: () => void;
  /** Handler when a geo filter is selected */
  handleSelectSingle: (option: IGeoFilter) => void;
}

/**
 * Custom hook that manages all state and logic for the GeoFilter component.
 *
 * Handles both controlled and uncontrolled modes:
 * - Controlled: Parent passes geoFilterId, included, and callbacks
 * - Uncontrolled: Component manages state via the targeting model
 *
 * @param props - The component props (either controlled or uncontrolled)
 * @returns Object containing state values and handlers
 *
 * @example
 * ```tsx
 * const {
 *   geofilter,
 *   isLoading,
 *   loadOptions,
 *   handleSelectSingle,
 * } = useGeoFilter(props);
 * ```
 */
export function useGeoFilter(props: IProps): IUseGeoFilterReturn {
  const { campaign } = props;
  const controlled = isControlled(props);

  const { GeoFilters, Targetables } = useStore();

  const [selectedType, setSelectedType] = useState<ITypeOption>(TYPE_OPTIONS[0]);

  const geofilter = (() => {
    if (controlled) {
      const controlledProps = props as IControlledProps;
      if (controlledProps.geoFilterId) {
        return GeoFilters.get(controlledProps.geoFilterId);
      }
      return undefined;
    }

    const uncontrolledProps = props as IUncontrolledProps;
    const targetableId = uncontrolledProps.targeting.get('target_data');
    if (!targetableId) {
      return undefined;
    }

    const targetable = Targetables.get(targetableId);
    const geoFilterId = targetable?.get('geo_filter');
    if (geoFilterId) {
      return GeoFilters.get(geoFilterId);
    }
    return undefined;
  })();

  const loadOptionsInternal = useCallback(
    async (inputValue: string): Promise<IGeoFilter[]> => {
      const filters: IFilters = {
        is_archived__ne: true,
      };

      if (inputValue) {
        filters.name__icontains = inputValue;
      }

      if (selectedType.id !== 'all') {
        filters.kind__in = [selectedType.id];
      }

      const geoFilters = GeoFilters.list({
        filters,
        pagination: {
          page: 0,
          page_size: 50,
        },
      });
      await when(() => !geoFilters.isPending);

      const usedGeoFilterIds = getUsedGeoFilterIds(props, (id) => Targetables.get(id));
      const currentGeoFilterId = getCurrentGeoFilterId(props, geofilter);

      return geoFilters.models
        .filter((gf) => !usedGeoFilterIds.has(gf.id) || gf.id === currentGeoFilterId)
        .map((gf) => gf.toJS());
    },
    [GeoFilters, Targetables, selectedType, props, geofilter],
  );

  const loadOptions = useMemo(() => debounce(loadOptionsInternal, 300), [loadOptionsInternal]);

  const handleIncludedChange = useCallback(
    (newValue?: string): void => {
      const isIncluded = newValue === 'included';
      if (controlled) {
        (props as IControlledProps).onIncludedChange(isIncluded);
      } else {
        (props as IUncontrolledProps).targeting.set({ included: isIncluded });
      }
    },
    [controlled, props],
  );

  const handleTypeChange = useCallback((option?: ITypeOption): void => {
    if (option) {
      setSelectedType(option);
    }
  }, []);

  const handleClear = useCallback((): void => {
    if (!controlled) {
      (props as IUncontrolledProps).targeting.set({
        name: '',
        target_data: undefined,
      });
    }
  }, [controlled, props]);

  const handleRemove = useCallback((): void => {
    if (controlled) {
      (props as IControlledProps).onRemove();
    } else {
      const uncontrolledProps = props as IUncontrolledProps;
      uncontrolledProps.onRemove(uncontrolledProps.targeting);
    }
  }, [controlled, props]);

  const handleSelectSingle = useCallback(
    (option: IGeoFilter): void => {
      if (controlled) {
        (props as IControlledProps).onSelect(option.id);
      } else {
        const uncontrolledProps = props as IUncontrolledProps;
        const newGeoTargetable = Targetables.create({
          _cls: TargetableClass.geo,
          name: option.name,
          geo_filter: option.id,
          partner: campaign?.get('parent_kind') === 'partner' ? campaign?.get('parent') : undefined,
        });
        uncontrolledProps.targeting.set({
          name: option.name,
          target_data: newGeoTargetable.get('id'),
        });
      }
    },
    [controlled, props, Targetables, campaign],
  );

  return {
    geofilter,
    selectedType,
    loadOptions,
    handleIncludedChange,
    handleTypeChange,
    handleClear,
    handleRemove,
    handleSelectSingle,
  };
}
